import Icon from '#components/Icon.tsx';
import { Button } from '#components/ui/button.tsx';
import { Calendar } from '#components/ui/calendar.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '#components/ui/popover.tsx';
import { format } from 'date-fns';
import { useState } from 'react';
import { DateRange } from 'react-day-picker';

type Mode = 'single' | 'range';
interface DatePickerProps {
  value?: Date | DateRange;
  onChange: (date: Date | DateRange | undefined) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  mode: Mode;
  fromDate?: Date;
  toDate?: Date;
  portalled?: boolean;
}

export function DatePicker({
  value,
  onChange,
  placeholder = 'Selecionar data',
  disabled = false,
  mode,
  fromDate,
  toDate,
  portalled = true,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);

  let formattedValue: string | null = null;

  if (mode === 'single' && value instanceof Date) {
    formattedValue = format(value, 'dd/MM/yyyy');
  }

  if (mode === 'range' && value && typeof value === 'object' && 'from' in value) {
    formattedValue = value.from
      ? value.to
        ? `${format(value.from, 'dd/MM/yyyy')} - ${format(value.to, 'dd/MM/yyyy')}`
        : `${format(value.from, 'dd/MM/yyyy')} - ...`
      : null;
  }

  return (
    <div className="w-full">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger className="border" asChild>
          <Button
            variant={'outline'}
            className="flex h-10 w-full cursor-pointer items-center justify-between rounded-[5px] text-[var(--input-color)]"
            disabled={disabled}
          >
            {formattedValue ?? <span className="text-[var(--input-placeholder)]">{placeholder}</span>}
            <Icon fill={1} name="calendar_today" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start" portalled={portalled}>
          {mode === 'single' ? (
            <Calendar
              mode="single"
              selected={value as Date}
              onSelect={(date) => {
                onChange(date as Date);
                setOpen(false);
              }}
              fromDate={fromDate}
              toDate={toDate}
              initialFocus
            />
          ) : (
            <Calendar
              mode="range"
              selected={value as DateRange}
              onSelect={(range) => {
                onChange(range as DateRange);
                if ((range as DateRange)?.from && (range as DateRange)?.to) {
                  setOpen(false);
                }
              }}
              fromDate={fromDate}
              toDate={toDate}
              initialFocus
            />
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
